/**
 * Build filter options for campaign.
 */
import { computeDiscountLogic } from '~/helpers/vue';
import useDiscountCampaignsStore from '~stores/discount-campaigns';

export function buildFilterOptions(
  products,
  discountPreview = false,
  performanceMode = false,
) {
  const filterOptions = new Set();

  products.forEach((product) => {
    const productDiscount = performanceMode
      ? product.discount
      : getProductDiscount(product, discountPreview);
    if (productDiscount > 0) {
      filterOptions.add(productDiscount.toString());
    }
  });

  // on this page we will always have numbers as filter values, so sort them this way
  return {
    Discount: Array.from(filterOptions).sort(
      (a, b) => parseFloat(a) - parseFloat(b),
    ),
  };
}

export function getProductDiscount(product, discountPreview) {
  const discountLogic = computeDiscountLogic({
    discountPreview,
    onSale: product.selectedOrFirstAvailableVariant?.compareAtPriceDiscount,
    product,
    variant: product.selectedOrFirstAvailableVariant,
  });

  const discountStore = useDiscountCampaignsStore();
  const { active } = discountStore;

  const productDiscount = discountStore.getCampaignDiscount(
    discountLogic,
    active ? 'active' : 'promoted',
  );
  return productDiscount;
}

/**
 * Compute contentList based on filters set.
 * @returns {String}
 */
export function filteredProducts(
  products,
  filters,
  state = 'ready',
  performanceMode = false,
) {
  if ((state !== 'ready' && !performanceMode) || !products) {
    return [];
  }

  if (
    !filters?.Discount ||
    filters?.Discount?.length === 0 ||
    performanceMode
  ) {
    return products.filter(
      (product) =>
        !product.selectedOrFirstAvailableVariant?.compareAtPriceDiscount ||
        !filters?.Discount,
    );
  }

  return products?.filter((product) => {
    const productDiscount = getProductDiscount(product);

    if (
      filters.Discount.find(
        (discount) => discount === productDiscount.toString(),
      )
    ) {
      return product;
    }
    return false;
  });
}

/**
 * Update URL. Reset own URL parameters while retaining other ones.
 * @param {Object} removeFilters - Filters to remove from the URL.
 * @param {Number} pageNumber - Page number to set in the URL.
 * @param {Object} filters - Filters to set in the URL.
 */
export function updateURL(removeFilters, pageNumber, filters) {
  const url = new URL(window.location.href);
  // clear only our own URL parameters
  url.searchParams.delete('page');
  Object.keys(removeFilters).forEach((key) => {
    url.searchParams.delete(key);
  });

  if (pageNumber > 1) {
    url.searchParams.set('page', pageNumber);
  }

  // Add filters back with the right values if there are any
  Object.keys(filters).forEach((key) => {
    if (filters[key].length) {
      url.searchParams.set(key, filters[key].join(','));
    }
  });
  window.history.pushState({}, '', url);
}

/**
 * Parse URL parameters for the filters.
 * @param {Object} filterOptions - Filter options to check against.
 */
export function parseURLParams(filterOptions) {
  const urlParams = new URLSearchParams(window.location.search);
  const filters = {};

  for (const [key, value] of urlParams.entries()) {
    // accept only valid filter keys
    if (key !== 'page') {
      if (key in filterOptions) {
        filters[key] = value.split(',');
      }
    }
  }

  return filters;
}

export function getPageNumber() {
  const urlParams = new URLSearchParams(window.location.search);
  return parseInt(urlParams.get('page')) || 1;
}
